/**
 * Echo Event Type Registry
 *
 * Centralized registry of all audit event types with full type safety.
 * Provides autocomplete and prevents typos.
 *
 * @example
 * ```typescript
 * import { ECHO_EVENTS } from '@nexus/types';
 *
 * @Echo({
 *   eventType: ECHO_EVENTS.USER.CREATE, // Autocomplete! ✅
 *   // ...
 * })
 * ```
 */

/**
 * Authentication Events
 */
export const ECHO_AUTH_EVENTS = {
  USER_LOGIN: "user.login",
  USER_LOGOUT: "user.logout",
  USER_PASSWORD_CHANGE: "user.password.change",
  USER_PASSWORD_RESET: "user.password.reset",
  API_KEY_CREATE: "api.key.create",
  API_KEY_UPDATE: "api.key.update",
  API_KEY_REVOKE: "api.key.revoke",
  API_AUTHENTICATE: "api.authenticate",
  SESSION_CREATE: "session.create",
  SESSION_EXPIRE: "session.expire"
} as const;

/**
 * User Management Events
 */
export const ECHO_USER_EVENTS = {
  CREATE: "user.create",
  UPDATE: "user.update",
  DELETE: "user.delete",
  INVITE: "user.invite",
  ACTIVATE: "user.activate",
  DEACTIVATE: "user.deactivate",
  // PERMISSION_GRANT / PERMISSION_REVOKE describe the full-scope lifecycle
  // actions: initial grant of admin access (createAdminUser) and full
  // revocation (deleteAdminUser). The events below cover the narrower
  // operations that sit between the two — re-assigning a policy vs.
  // adding/removing a single permission override — so compliance queries
  // filtered by eventType can tell structurally different actions apart.
  PERMISSION_GRANT: "user.permission.grant",
  PERMISSION_REVOKE: "user.permission.revoke",
  PERMISSION_POLICY_ASSIGN: "user.permission.policy.assign",
  PERMISSION_OVERRIDE_CREATE: "user.permission.override.create",
  PERMISSION_OVERRIDE_DELETE: "user.permission.override.delete",
  ROLE_CHANGE: "user.role.change",
  ONBOARDING_COMPLETE: "user.onboarding.complete",
  CREDENTIAL_UPDATE: "user.credential.update",
  WEBHOOK_SETTINGS_UPDATE: "user.webhook.settings.update",
  UI_PREFERENCES_UPDATE: "user.ui.preferences.update",
  MACRO_CREATE: "user.macro.create",
  MACRO_UPDATE: "user.macro.update",
  MACRO_DELETE: "user.macro.delete"
} as const;

/**
 * Organization Events
 */
export const ECHO_ORG_EVENTS = {
  CREATE: "organization.create",
  UPDATE: "organization.update",
  DELETE: "organization.delete",
  MEMBER_ADD: "organization.member.add",
  MEMBER_REMOVE: "organization.member.remove",
  MEMBER_ROLE_CHANGE: "organization.member.role.change",
  CONFIG_UPDATE: "organization.config.update",
  RETENTION_POLICY_UPDATE: "organization.retention_policy.update"
} as const;

/**
 * Chat Events
 */
export const ECHO_CHAT_EVENTS = {
  CREATE: "chat.create",
  UPDATE: "chat.update",
  DELETE: "chat.delete",
  /**
   * A conversation was HARD deleted — its row and everything holding its content
   * removed. Emitted by the chat-retention cron with the system as the actor; its
   * own id so an auditor can find a shred without reading every soft delete.
   */
  SHRED: "chat.shred",
  CLOSE: "chat.close",
  ARCHIVE: "chat.archive",
  REOPEN: "chat.reopen",
  MEMBER_ADD: "chat.member.add",
  MEMBER_REMOVE: "chat.member.remove",
  MESSAGE_SEND: "chat.message.send",
  MESSAGE_EDIT: "chat.message.edit",
  MESSAGE_DELETE: "chat.message.delete",
  MESSAGE_REGENERATE: "chat.message.regenerate",
  MESSAGE_VALIDATE_SEND: "chat.message.validate_and_send",
  SETTINGS_UPDATE: "chat.settings.update",
  STATUS_UPDATE: "chat.status.update",
  ASSIGNED_USERS_UPDATE: "chat.assigned_users.update",
  COMMENT_ADD: "chat.comment.add",
  SATISFACTION_RATE: "chat.satisfaction.rate",
  EXPORT: "chat.export",
  WHATSAPP_TEMPLATE_SEND: "chat.whatsapp_template.send"
} as const;

/**
 * Workflow Events
 */
export const ECHO_WORKFLOW_EVENTS = {
  CREATE: "workflow.create",
  UPDATE: "workflow.update",
  DELETE: "workflow.delete",
  PUBLISH: "workflow.publish",
  UNPUBLISH: "workflow.unpublish",
  EXECUTE: "workflow.execute",
  CANCEL: "workflow.cancel",
  DUPLICATE: "workflow.duplicate",
  RESTORE: "workflow.restore",
  NODE_EXECUTE: "workflow.node.execute",
  NODE_FAIL: "workflow.node.fail",
  NODE_TEST: "workflow.node.test",
  NODE_RETRY: "workflow.node.retry",
  EXECUTION_CANCEL: "workflow.execution.cancel",
  EXECUTION_DELETE: "workflow.execution.delete",
  EXECUTION_RETRIGGER: "workflow.execution.retrigger",
  TEST_START: "workflow.test.start",
  TRIGGER_DEPLOY: "workflow.trigger.deploy",
  TRIGGER_DELETE: "workflow.trigger.delete",
  TRIGGER_SECRET_READ: "workflow.trigger.secret.read",
  TRIGGER_SECRET_ROTATE: "workflow.trigger.secret.rotate",
  TRIGGER_SECRET_ENFORCE: "workflow.trigger.secret.enforce",
  AGENT_ATTACH: "workflow.agent.attach",
  AI_GENERATE_CODE: "workflow.ai.generate_code"
} as const;

/**
 * Workflow Preset Events
 *
 * Lifecycle of preset-materialised workflows (settings-driven workflow
 * generation, e.g. satisfaction evaluation). Distinct from ECHO_WORKFLOW_EVENTS
 * so compliance queries can separate operator-edited workflows from
 * preset-managed ones, and budget-cap breaches are filterable in isolation.
 *
 *   - CREATE  / UPDATE — preset config changed at the deployment or org level,
 *                        materialiser produced or refreshed the managed workflow.
 *   - DETACH           — operator clicked "Detach into editable" in the read-only
 *                        editor, flipping isManaged=false + clearing presetSource.
 *   - AI_BUDGET_EXCEEDED — pre-flight cap check short-circuited an AI inference
 *                          run; downstream nodes record this rather than firing.
 */
export const ECHO_WORKFLOW_PRESET_EVENTS = {
  CREATE: "workflow.preset.create",
  UPDATE: "workflow.preset.update",
  DETACH: "workflow.preset.detach",
  AI_BUDGET_EXCEEDED: "workflow.preset.ai_budget_exceeded"
} as const;

/**
 * Document Events
 */
export const ECHO_DOCUMENT_EVENTS = {
  CREATE: "document.create",
  UPLOAD: "document.upload",
  ACCESS: "document.access",
  DOWNLOAD: "document.download",
  UPDATE: "document.update",
  DELETE: "document.delete",
  SHARE: "document.share",
  UNSHARE: "document.unshare",
  MOVE: "document.move",
  PROCESS: "document.process",
  WEBSITE_ADD: "document.website.add",
  CLOUD_CONNECT: "document.cloud.connect",
  SYNC_ENABLE: "document.sync.enable",
  SYNC_DISABLE: "document.sync.disable",
  SYNC_INTERVAL_UPDATE: "document.sync.interval.update",
  SYNC_TRIGGER: "document.sync.trigger"
} as const;

/**
 * Asset Events (org-scoped public asset library — NEX-2540)
 */
export const ECHO_ASSET_EVENTS = {
  UPLOAD: "asset.upload",
  DELETE: "asset.delete"
} as const;

/**
 * Deployment Events
 */
export const ECHO_DEPLOYMENT_EVENTS = {
  CREATE: "deployment.create",
  UPDATE: "deployment.update",
  DELETE: "deployment.delete",
  DUPLICATE: "deployment.duplicate",
  ACTIVATE: "deployment.activate",
  DEACTIVATE: "deployment.deactivate",
  EXECUTE: "deployment.execute",
  SLACK_SEND: "slack.message.send",
  EMAIL_SEND: "email.send",
  WEBHOOK_TRIGGER: "webhook.trigger",
  FOLDER_CREATE: "deployment.folder.create",
  FOLDER_UPDATE: "deployment.folder.update",
  FOLDER_DELETE: "deployment.folder.delete",
  TEAM_ASSIGN: "deployment.team.assign",
  TEAM_REMOVE: "deployment.team.remove",
  CONNECTION_CREATE: "deployment.connection.create",
  CONNECTION_UPDATE: "deployment.connection.update",
  CONNECTION_DELETE: "deployment.connection.delete",
  /** Emitted on the previous owner when a WhatsApp sender is reassigned away. */
  SENDER_DETACHED: "deployment.sender.detached",
  /**
   * A deployment's chat-retention policy was created or replaced. Its own id,
   * not `UPDATE`: a SHRED policy hard-deletes conversations, so an auditor has
   * to be able to find this change without reading every settings edit.
   */
  RETENTION_POLICY_UPDATE: "deployment.retention_policy.update"
} as const;

/**
 * Agent Events
 */
export const ECHO_AGENT_EVENTS = {
  CREATE: "agent.create",
  UPDATE: "agent.update",
  DELETE: "agent.delete",
  STOP: "agent.stop",
  DUPLICATE: "agent.duplicate",
  ASSIGN: "agent.assign",
  UNASSIGN: "agent.unassign",
  CONFIG_UPDATE: "agent.config.update",
  PROFILE_PICTURE_UPLOAD: "agent.profile_picture.upload",
  PROFILE_PICTURE_REGENERATE: "agent.profile_picture.regenerate",
  // Code Interpreter Events
  CODE_INTERPRETER_INVOKED: "agent.code_interpreter.invoked",
  CODE_INTERPRETER_COMPLETED: "agent.code_interpreter.completed",
  CODE_INTERPRETER_FAILED: "agent.code_interpreter.failed",
  // Anthropic Proxy Events
  ANTHROPIC_PROXY_REQUEST: "agent.anthropic_proxy.request",
  // Folder Events
  FOLDER_CREATE: "agent.folder.create",
  FOLDER_UPDATE: "agent.folder.update",
  FOLDER_DELETE: "agent.folder.delete",
  // Tool Events
  TOOL_CREATE: "agent.tool.create",
  TOOL_UPDATE: "agent.tool.update",
  TOOL_DELETE: "agent.tool.delete",
  TOOL_DELETE_ALL: "agent.tool.delete.all",
  // Collection Events
  COLLECTION_ATTACH: "agent.collection.attach",
  COLLECTION_DETACH: "agent.collection.detach",
  // Workspace Events (agent ↔ workspace mount assignments)
  WORKSPACE_ATTACH: "agent.workspace.attach",
  WORKSPACE_DETACH: "agent.workspace.detach",
  // Claude Code skill Events (per-agent custom skill bundles)
  SKILL_CREATE: "agent.skill.create",
  SKILL_UPDATE: "agent.skill.update",
  SKILL_DELETE: "agent.skill.delete",
  // Version Events
  VERSION_CREATE: "agent.version.create",
  VERSION_PUBLISH: "agent.version.publish",
  VERSION_RESTORE: "agent.version.restore",
  VERSION_DELETE: "agent.version.delete",
  // Golden conversation Events (Prompt Lab phase 2)
  GOLDEN_CONVERSATION_CREATE: "agent.golden_conversation.create",
  GOLDEN_CONVERSATION_UPDATE: "agent.golden_conversation.update",
  GOLDEN_CONVERSATION_DELETE: "agent.golden_conversation.delete",

  // Prompt eval run Events (Prompt Lab phase 3)
  PROMPT_EVAL_RUN_CREATE: "agent.prompt_eval_run.create",
  PROMPT_EVAL_RUN_ABORT: "agent.prompt_eval_run.abort"
} as const;

/**
 * Workspace Events (the shared file drive — REST lifecycle + WebDAV mount)
 */
export const ECHO_WORKSPACE_EVENTS = {
  CREATE: "workspace.create",
  UPDATE: "workspace.update",
  DELETE: "workspace.delete"
} as const;

/**
 * Vault (Credential) Events
 */
export const ECHO_VAULT_EVENTS = {
  SECRET_CREATE: "vault.secret.create",
  SECRET_UPDATE: "vault.secret.update",
  SECRET_DELETE: "vault.secret.delete",
  // POOL_CLAIM / POOL_RELEASE describe org-side slot consumption: an
  // organization claims a slot from the shared pool, or releases its
  // claim. The POOL_SLOT_* events below describe admin-side pool
  // inventory management (add/edit/remove slots, force-release assigned
  // slots during support). Distinct eventTypes let compliance queries
  // separate "org activity on the pool" from "admin operations on the
  // pool itself" without falling back to request-path inspection.
  POOL_CLAIM: "vault.pool.claim",
  POOL_RELEASE: "vault.pool.release",
  POOL_SLOT_CREATE: "vault.pool.slot.create",
  POOL_SLOT_UPDATE: "vault.pool.slot.update",
  POOL_SLOT_DELETE: "vault.pool.slot.delete",
  POOL_SLOT_FORCE_RELEASE: "vault.pool.slot.force_release"
} as const;

/**
 * Access Card Events
 *
 * An access card is a scoped delegation of ONE credential: it names the actions
 * a holder may take and the values it may take them with, without ever handing
 * over the credential itself. Separate from ECHO_VAULT_EVENTS because the vault
 * records what the org STORES and these record what it DELEGATES — a compliance
 * question ("who was allowed to act as this credential, and when did that stop?")
 * that cannot be answered by filtering secret lifecycle events.
 *
 * REVOKE and DELETE are both real and are not synonyms. Revocation is a
 * tombstone: the card stops authorizing immediately and every audit row that
 * references it still resolves. Deletion removes the row and cascades the grants
 * built on it. Collapsing them would make "retired, still explicable" and "gone"
 * indistinguishable in an export.
 */
export const ECHO_ACCESS_CARD_EVENTS = {
  CREATE: "access_card.create",
  UPDATE: "access_card.update",
  REVOKE: "access_card.revoke",
  DELETE: "access_card.delete"
} as const;

/**
 * Vibe Card Events — an access card projected into a Vibe app's environment
 *
 * The AUTHORITY is a grant (one app's authority to use one card); the
 * PROJECTION is a binding (that authority appearing on the app's environment
 * screen under a name the author picks). Both move together on import, and
 * separately afterwards, which is why the vocabulary names them separately.
 *
 * GRANT_TRANSITION is deliberately ONE event carrying its reason, not one event
 * per reason. There is a single state machine behind manual revocation, quota
 * and budget exhaustion, card deletion, card revocation, app deletion and key
 * revocation; giving each trigger its own event type would invite a second code
 * path to appear behind it, and would make "when did this app lose access, for
 * any reason?" a query over a list someone must remember to extend.
 *
 * **Nothing here records an individual brokered CALL, and that is deliberate.**
 * `@Echo` is fire-and-forget: it cannot fail a request and it gates nothing, so
 * it is observability and never enforcement. The per-call record is a
 * `BrokerInvocation` row written inside the dispatching transaction, which is
 * what makes the call path fail CLOSED. An Echo event for the same call would be
 * a second, weaker copy of an audit that already exists — and the copy is the one
 * a reader would reach for.
 */
export const ECHO_VIBE_CARD_EVENTS = {
  IMPORT: "vibe.card.import",
  BINDING_UPDATE: "vibe.card.binding.update",
  BINDING_DELETE: "vibe.card.binding.delete",
  GRANT_APPROVE: "vibe.card.grant.approve",
  GRANT_TRANSITION: "vibe.card.grant.transition"
} as const;

/**
 * Payment Events
 */
export const ECHO_PAYMENT_EVENTS = {
  CREATE: "payment.create",
  PROCESS: "payment.process",
  FAIL: "payment.fail",
  REFUND: "payment.refund",
  SUBSCRIPTION_CREATE: "subscription.create",
  SUBSCRIPTION_CANCEL: "subscription.cancel",
  SUBSCRIPTION_RENEW: "subscription.renew",
  CHECKOUT_CREATE: "payment.checkout.create",
  SUBSCRIPTION_UPGRADE: "subscription.upgrade",
  SUBSCRIPTION_DOWNGRADE: "subscription.downgrade",
  TOPUP: "payment.topup",
  AUTO_TOPUP_TOGGLE: "payment.auto.topup.toggle"
} as const;

/**
 * System Events
 */
export const ECHO_SYSTEM_EVENTS = {
  CRON_RUN: "cron.run",
  MIGRATION_RUN: "migration.run",
  BACKUP_CREATE: "backup.create",
  BACKUP_RESTORE: "backup.restore",
  CONFIG_UPDATE: "system.config.update"
} as const;

/**
 * Permission Events
 */
export const ECHO_PERMISSION_EVENTS = {
  GRANT: "permission.grant",
  REVOKE: "permission.revoke",
  // An admin's decision to keep a feature withheld from a group. Neither a
  // GRANT (nothing is written) nor a REVOKE (nothing held is withdrawn).
  DECLINE: "permission.decline",
  WILDCARD_GRANT: "permission.wildcard.grant",
  ACCESS_UPDATE: "permission.access.update",
  ORG_SETTINGS_UPDATE: "permission.org.settings.update",
  RESOURCE_VISIBILITY_UPDATE: "permission.resource.visibility.update",
  // Admin policy lifecycle — distinct from ORG_SETTINGS_UPDATE so
  // compliance queries can tell "policy deleted" apart from a generic
  // settings change when filtering by eventType.
  POLICY_CREATE: "permission.policy.create",
  POLICY_UPDATE: "permission.policy.update",
  POLICY_DELETE: "permission.policy.delete"
} as const;

/**
 * User Group Events
 */
export const ECHO_USER_GROUP_EVENTS = {
  CREATE: "user.group.create",
  UPDATE: "user.group.update",
  DELETE: "user.group.delete",
  MEMBER_ADD: "user.group.member.add",
  MEMBER_REMOVE: "user.group.member.remove"
} as const;

/**
 * OAuth Events
 */
export const ECHO_OAUTH_EVENTS = {
  CONNECTION_CREATE: "oauth.connection.create",
  CONNECTION_DELETE: "oauth.connection.delete"
} as const;

/**
 * Knowledge Events
 */
export const ECHO_KNOWLEDGE_EVENTS = {
  UPLOAD: "knowledge.upload",
  DELETE: "knowledge.delete",
  PROCESS: "knowledge.process"
} as const;

/**
 * Document Template Events
 */
export const ECHO_TEMPLATE_EVENTS = {
  CREATE: "template.create",
  UPDATE: "template.update",
  DELETE: "template.delete",
  FILE_UPLOAD: "template.file.upload",
  GENERATE: "template.generate",
  FOLDER_CREATE: "template.folder.create",
  FOLDER_UPDATE: "template.folder.update",
  FOLDER_DELETE: "template.folder.delete"
} as const;

/**
 * Custom Model Events
 */
export const ECHO_CUSTOM_MODEL_EVENTS = {
  CREATE: "custom.model.create",
  UPDATE: "custom.model.update",
  DELETE: "custom.model.delete"
} as const;

/**
 * AI Task Events
 */
export const ECHO_AI_TASK_EVENTS = {
  CREATE: "ai_task.create",
  UPDATE: "ai_task.update",
  DELETE: "ai_task.delete",
  DUPLICATE: "ai_task.duplicate",
  TEST_EXECUTE: "ai_task.test.execute",
  PROMPT_UPDATE: "ai_task.prompt.update",
  MODEL_CONFIG_UPDATE: "ai_task.model_config.update",
  VERSION_CREATE: "ai_task.version.create",
  VERSION_PUBLISH: "ai_task.version.publish",
  VERSION_RESTORE: "ai_task.version.restore",
  VERSION_DELETE: "ai_task.version.delete",
  EVALUATION_CREATE: "ai_task.evaluation.create",
  EVALUATION_EXECUTE: "ai_task.evaluation.execute",
  EVALUATION_DELETE: "ai_task.evaluation.delete"
} as const;

/**
 * Slack Events
 */
export const ECHO_SLACK_EVENTS = {
  APP_CREATE: "slack.app.create",
  APP_DELETE: "slack.app.delete",
  APP_INSTALL: "slack.app.install",
  WORKSPACE_CONNECT: "slack.workspace.connect",
  WORKSPACE_DELETE: "slack.workspace.delete",
  BOT_CREATE: "slack.bot.create",
  BOT_PROFILE_UPDATE: "slack.bot.profile.update",
  MANIFEST_SYNC: "slack.manifest.sync"
} as const;

/**
 * WhatsApp Events (senders, templates — connections are under DEPLOYMENT)
 */
export const ECHO_WHATSAPP_EVENTS = {
  CONNECTION_CREATE: "whatsapp.connection.create",
  CONNECTION_UPDATE: "whatsapp.connection.update",
  CONNECTION_DELETE: "whatsapp.connection.delete",
  SENDER_CREATE: "whatsapp.sender.create",
  SENDER_DELETE: "whatsapp.sender.delete",
  SENDER_PROFILE_UPDATE: "whatsapp.sender.profile.update",
  SENDER_VERIFY: "whatsapp.sender.verify",
  /**
   * A sender's inbound webhook was pointed back at its connection's route.
   *
   * Worth its own event because it is the one write that decides whether the
   * sender receives anything at all, and the state it repairs — a hook naming a
   * deployment that has since been detached — fails silently: inbound is
   * answered 200 by a handler that finds no connection, so nothing else in the
   * system records that the line was dead.
   */
  SENDER_WEBHOOK_REPOINT: "whatsapp.sender.webhook.repoint",
  TEMPLATE_CREATE: "whatsapp.template.create",
  TEMPLATE_DELETE: "whatsapp.template.delete",
  TEMPLATE_SUBMIT_APPROVAL: "whatsapp.template.submit_approval"
} as const;

/**
 * Phone Number Events
 */
export const ECHO_PHONE_EVENTS = {
  NUMBER_BUY: "phone.number.buy",
  NUMBER_TRANSFER: "phone.number.transfer",
  NUMBER_RELEASE: "phone.number.release",
  /** Marked for release — NOT yet released. See `NUMBER_RELEASE` for the actual release. */
  NUMBER_MARK_FOR_RELEASE: "phone.number.mark_for_release",
  NUMBER_CANCEL_PENDING_RELEASE: "phone.number.cancel_pending_release",
  /**
   * A number's voice / SMS / status-callback hooks were repointed.
   *
   * The vocabulary covered buy, transfer and release but not this — the one
   * change that silently decides whether a live number reaches us at all. A
   * number whose hook points nowhere is indistinguishable from a healthy one
   * until a customer reports missed calls, so the edit has to be attributable.
   */
  NUMBER_CONFIGURE: "phone.number.configure",
  COMPLIANCE_SUBMIT: "phone.compliance.submit",
  A2P_SUBMIT: "phone.a2p.submit",
  ADDRESS_CREATE: "phone.address.create",
  ADDRESS_DELETE: "phone.address.delete",
  REQUEST_CREATE: "phone.request.create",
  REQUEST_APPROVE: "phone.request.approve",
  REQUEST_REJECT: "phone.request.reject"
} as const;

/**
 * Call Events
 */
export const ECHO_CALL_EVENTS = {
  INITIATE: "call.initiate",
  END: "call.end",
  TRANSFER: "call.transfer",
  SMS_SEND: "call.sms.send",
  RECORDING_ACCESS: "call.recording.access"
} as const;

/**
 * External Tool Events
 */
export const ECHO_EXTERNAL_TOOL_EVENTS = {
  CREATE: "external_tool.create",
  UPDATE: "external_tool.update",
  DELETE: "external_tool.delete",
  CREDENTIAL_DELETE: "external_tool.credential.delete",
  PIPEDREAM_CREDENTIAL_CREATE: "pipedream.credential.create",
  PIPEDREAM_TRIGGER_DELETE: "pipedream.trigger.delete"
} as const;

/**
 * Knowledge Collection Events (standalone, not agent.collection)
 */
export const ECHO_COLLECTION_EVENTS = {
  CREATE: "collection.create",
  UPDATE: "collection.update",
  DELETE: "collection.delete",
  DOCUMENT_ADD: "collection.document.add",
  DOCUMENT_REMOVE: "collection.document.remove"
} as const;

/**
 * Teams Events
 */
export const ECHO_TEAMS_EVENTS = {
  BOT_CREATE: "teams.bot.create",
  PACKAGE_PUBLISH: "teams.package.publish",
  USERS_BULK_IMPORT: "teams.users.bulk_import"
} as const;

/**
 * API Key Connection Events (frontend registry uses separate prefix)
 */
export const ECHO_API_KEY_CONNECTION_EVENTS = {
  CREATE: "api_key_connection.create",
  DELETE: "api_key_connection.delete"
} as const;

/**
 * Bug Report Events
 *
 * Customer-facing bug report submission. Separate from SYSTEM (which is
 * platform-level infrastructure) and from USER (which is account lifecycle)
 * — a bug report is a support artifact produced by the end user.
 */
export const ECHO_BUG_REPORT_EVENTS = {
  SUBMIT: "bug_report.submit"
} as const;

/**
 * Echo System Events (Meta)
 */
export const ECHO_META_EVENTS = {
  CONFIG_UPDATE: "echo.config.update",
  EXPORT_CREATE: "echo.export.create",
  EXPORT_COMPLETE: "echo.export.complete",
  EXPORT_FAILED: "echo.export.failed",
  EXPORT_DOWNLOAD: "echo.export.download",
  PARTITION_CREATE: "echo.partition.create",
  PARTITION_ARCHIVE: "echo.partition.archive",
  ALERT_TRIGGER: "echo.alert.trigger"
} as const;

/**
 * Complete Event Registry
 *
 * All event types organized by category with full type safety.
 */
export const ECHO_EVENTS = {
  AUTH: ECHO_AUTH_EVENTS,
  USER: ECHO_USER_EVENTS,
  ORG: ECHO_ORG_EVENTS,
  CHAT: ECHO_CHAT_EVENTS,
  WORKFLOW: ECHO_WORKFLOW_EVENTS,
  WORKFLOW_PRESET: ECHO_WORKFLOW_PRESET_EVENTS,
  DOCUMENT: ECHO_DOCUMENT_EVENTS,
  ASSET: ECHO_ASSET_EVENTS,
  DEPLOYMENT: ECHO_DEPLOYMENT_EVENTS,
  AGENT: ECHO_AGENT_EVENTS,
  WORKSPACE: ECHO_WORKSPACE_EVENTS,
  VAULT: ECHO_VAULT_EVENTS,
  ACCESS_CARD: ECHO_ACCESS_CARD_EVENTS,
  VIBE_CARD: ECHO_VIBE_CARD_EVENTS,
  PAYMENT: ECHO_PAYMENT_EVENTS,
  PERMISSION: ECHO_PERMISSION_EVENTS,
  USER_GROUP: ECHO_USER_GROUP_EVENTS,
  OAUTH: ECHO_OAUTH_EVENTS,
  KNOWLEDGE: ECHO_KNOWLEDGE_EVENTS,
  TEMPLATE: ECHO_TEMPLATE_EVENTS,
  CUSTOM_MODEL: ECHO_CUSTOM_MODEL_EVENTS,
  AI_TASK: ECHO_AI_TASK_EVENTS,
  SLACK: ECHO_SLACK_EVENTS,
  WHATSAPP: ECHO_WHATSAPP_EVENTS,
  PHONE: ECHO_PHONE_EVENTS,
  CALL: ECHO_CALL_EVENTS,
  EXTERNAL_TOOL: ECHO_EXTERNAL_TOOL_EVENTS,
  COLLECTION: ECHO_COLLECTION_EVENTS,
  TEAMS: ECHO_TEAMS_EVENTS,
  API_KEY_CONNECTION: ECHO_API_KEY_CONNECTION_EVENTS,
  SYSTEM: ECHO_SYSTEM_EVENTS,
  BUG_REPORT: ECHO_BUG_REPORT_EVENTS,
  META: ECHO_META_EVENTS
} as const;

/**
 * Extract all event type values as a union type.
 * Uses distributive conditional to properly handle union of const objects.
 */
type ExtractValues<T> = T extends Record<string, infer V> ? V : never;
type AllCategoryEvents = (typeof ECHO_EVENTS)[keyof typeof ECHO_EVENTS];

export type EchoEventType = ExtractValues<AllCategoryEvents>;

/**
 * Event categories
 */
export const ECHO_EVENT_CATEGORIES = {
  AUTHENTICATION: "authentication",
  DATA: "data",
  SYSTEM: "system",
  DEPLOYMENT: "deployment",
  SECURITY: "security"
} as const;

export type EchoEventCategory = (typeof ECHO_EVENT_CATEGORIES)[keyof typeof ECHO_EVENT_CATEGORIES];

/**
 * Action types
 */
export const ECHO_ACTIONS = {
  CREATE: "create",
  UPDATE: "update",
  DELETE: "delete",
  ACCESS: "access",
  EXECUTE: "execute",
  AUTHENTICATE: "authenticate"
} as const;

export type EchoAction = (typeof ECHO_ACTIONS)[keyof typeof ECHO_ACTIONS];

/**
 * Severity levels
 */
export const ECHO_SEVERITY = {
  DEBUG: "debug",
  INFO: "info",
  WARNING: "warning",
  ERROR: "error",
  CRITICAL: "critical"
} as const;

export type EchoSeverity = (typeof ECHO_SEVERITY)[keyof typeof ECHO_SEVERITY];

/**
 * Status values
 */
export const ECHO_STATUS = {
  SUCCESS: "success",
  FAILURE: "failure",
  PENDING: "pending"
} as const;

export type EchoStatus = (typeof ECHO_STATUS)[keyof typeof ECHO_STATUS];
